import { useCallback, useEffect, useState } from "react";
import { api, errDetail } from "../../lib/api";
import { useApp } from "../../lib/store";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { Building2, Factory, Globe2, Plus, Search, Trash2 } from "lucide-react";

function SimpleList({ path, fields, labelKeys, testid }) {
  const { t } = useApp();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({});
  const load = useCallback(() => api.get(`/iam/${path}`).then((r) => setRows(r.data || [])).catch(() => {}), [path]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    try { await api.post(`/iam/${path}`, form); setForm({}); load(); toast.success("✓"); }
    catch (e) { toast.error(errDetail(e)); }
  };
  const del = async (id) => {
    try { await api.delete(`/iam/${path}/${id}`); load(); }
    catch (e) { toast.error(errDetail(e)); }
  };
  const upd = async (row, patch) => {
    try { await api.put(`/iam/${path}/${row.id}`, patch); load(); toast.success("✓"); }
    catch (e) { toast.error(errDetail(e)); }
  };

  return (
    <div className="space-y-3" data-testid={testid}>
      <div className="rounded-xl border border-gray-200 bg-white p-3 flex flex-wrap gap-2 items-end">
        {fields.map((f, i) => (
          <div key={f} className="space-y-1 flex-1 min-w-[140px]">
            <Label className="text-[11px]">{t(labelKeys[i])}</Label>
            <Input value={form[f] || ""} onChange={(e) => setForm((x) => ({ ...x, [f]: e.target.value }))}
              className="h-9" data-testid={`${testid}-${f}-input`} />
          </div>
        ))}
        <Button className="bg-[#E8871E] hover:bg-[#d1751a] text-white rounded-full" onClick={add}
          data-testid={`${testid}-add-btn`}><Plus className="h-4 w-4 mr-1" />{t("add")}</Button>
      </div>
      <div className="rounded-xl border border-gray-200 bg-white divide-y divide-gray-50">
        {rows.map((r) => (
          <div key={r.id} className="px-4 py-2.5 flex flex-wrap gap-3 items-center text-sm" data-testid={`${testid}-row-${r.id}`}>
            <Input defaultValue={r.name} className="h-8 max-w-[240px]"
              onBlur={(e) => e.target.value !== r.name && upd(r, { name: e.target.value })} />
            {fields.slice(1).map((f) => (
              <Input key={f} defaultValue={r[f] || ""} placeholder={f} className="h-8 max-w-[140px] text-xs"
                onBlur={(e) => e.target.value !== (r[f] || "") && upd(r, { [f]: e.target.value })} />
            ))}
            <button className="ml-auto text-gray-300 hover:text-red-500" onClick={() => del(r.id)}
              data-testid={`${testid}-del-${r.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        {!rows.length && <p className="px-4 py-4 text-xs text-gray-400">–</p>}
      </div>
    </div>
  );
}

function PlantsTab() {
  const { t, picklists, plOptions } = useApp();
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [entities, setEntities] = useState([]);
  const [regions, setRegions] = useState([]);
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    const p = new URLSearchParams({ page, page_size: 25 });
    if (q) p.set("q", q);
    if (country) p.set("country", country);
    if (status) p.set("status", status);
    api.get(`/iam/plants?${p.toString()}`).then((r) => setData(r.data)).catch(() => {});
  }, [q, country, status, page]);

  useEffect(() => { const id = setTimeout(load, 200); return () => clearTimeout(id); }, [load]);
  useEffect(() => {
    api.get("/iam/legal-entities").then((r) => setEntities(r.data || [])).catch(() => {});
    api.get("/iam/regions").then((r) => setRegions(r.data || [])).catch(() => {});
    api.get("/iam/users?page_size=200").then((r) => setUsers(r.data.items || [])).catch(() => {});
  }, []);

  const save = async () => {
    try {
      if (editing.id) await api.put(`/iam/plants/${editing.id}`, editing);
      else await api.post("/iam/plants", editing);
      toast.success("✓"); setEditing(null); load();
    } catch (e) { toast.error(errDetail(e)); }
  };
  const del = async (p) => {
    try { await api.delete(`/iam/plants/${p.id}`); load(); }
    catch (e) { toast.error(errDetail(e)); }
  };

  const countries = [...new Set(data.items.map((p) => p.country).filter(Boolean))];

  return (
    <div className="space-y-3" data-testid="org-plants-tab">
      <div className="rounded-xl border border-gray-200 bg-white p-3 flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} className="pl-8 h-9"
            placeholder={t("iam_search_plants")} data-testid="plants-search-input" />
        </div>
        <Select value={country || "any"} onValueChange={(v) => { setCountry(v === "any" ? "" : v); setPage(1); }}>
          <SelectTrigger className="h-9 w-32 text-xs" data-testid="plants-country-filter"><SelectValue placeholder={t("iam_country")} /></SelectTrigger>
          <SelectContent><SelectItem value="any">{t("iam_country")}: {t("all")}</SelectItem>
            {countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={status || "any"} onValueChange={(v) => { setStatus(v === "any" ? "" : v); setPage(1); }}>
          <SelectTrigger className="h-9 w-32 text-xs" data-testid="plants-status-filter"><SelectValue placeholder={t("status")} /></SelectTrigger>
          <SelectContent><SelectItem value="any">{t("status")}: {t("all")}</SelectItem>
            <SelectItem value="active">{t("iam_status_active")}</SelectItem>
            <SelectItem value="inactive">{t("iam_status_inactive")}</SelectItem></SelectContent>
        </Select>
        <Button className="bg-[#E8871E] hover:bg-[#d1751a] text-white rounded-full"
          onClick={() => setEditing({ code: "", name: "", status: "active", segments: [] })} data-testid="plant-new-btn">
          <Plus className="h-4 w-4 mr-1" />{t("add")}
        </Button>
      </div>

      <p className="text-xs text-gray-500">{data.total} {t("iam_plants")}</p>

      <div className="hidden md:block rounded-xl border border-gray-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="bg-gray-50/70 border-b border-gray-100 text-xs text-gray-500">
            <th className="px-3 py-2 text-left">{t("iam_plant_code")}</th>
            <th className="px-3 py-2 text-left">{t("name")}</th>
            <th className="px-3 py-2 text-left">{t("iam_legal_entities")}</th>
            <th className="px-3 py-2 text-left">{t("region")}</th>
            <th className="px-3 py-2 text-left">{t("iam_country")}</th>
            <th className="px-3 py-2 text-left">{t("iam_plant_manager")}</th>
            <th className="px-3 py-2 text-left">{t("status")}</th>
            <th className="px-3 py-2"></th>
          </tr></thead>
          <tbody>
            {data.items.map((p) => (
              <tr key={p.id} className="border-b border-gray-50 hover:bg-amber-50/30" data-testid={`plant-row-${p.code}`}>
                <td className="px-3 py-2 font-mono text-[#E8871E] font-semibold cursor-pointer" onClick={() => setEditing(p)}>{p.code}</td>
                <td className="px-3 py-2 cursor-pointer" onClick={() => setEditing(p)}>{p.name}</td>
                <td className="px-3 py-2 text-xs">{p.legal_entity_name || "–"}</td>
                <td className="px-3 py-2 text-xs">{p.region_name || "–"}</td>
                <td className="px-3 py-2 text-xs">{p.country}</td>
                <td className="px-3 py-2 text-xs">{p.manager_name || "–"}</td>
                <td className="px-3 py-2">
                  <Badge className={`${p.status === "inactive" ? "bg-gray-100 text-gray-500" : "bg-emerald-50 text-emerald-700"} hover:bg-inherit text-[10px]`}>
                    {t(`iam_status_${p.status || "active"}`)}
                  </Badge>
                </td>
                <td className="px-3 py-2 text-right">
                  <button className="text-gray-300 hover:text-red-500" onClick={() => del(p)} data-testid={`plant-del-${p.code}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="md:hidden space-y-2">
        {data.items.map((p) => (
          <button key={p.id} onClick={() => setEditing(p)} className="w-full text-left rounded-xl border border-gray-200 bg-white p-3"
            data-testid={`plant-card-${p.code}`}>
            <div className="flex items-center gap-2">
              <span className="font-mono font-bold text-[#E8871E]">{p.code}</span>
              <span className="font-medium">{p.name}</span>
              <Badge className={`ml-auto ${p.status === "inactive" ? "bg-gray-100 text-gray-500" : "bg-emerald-50 text-emerald-700"} hover:bg-inherit text-[10px]`}>
                {t(`iam_status_${p.status || "active"}`)}
              </Badge>
            </div>
            <p className="text-[11px] text-gray-500 mt-1">{p.region_name} · {p.country} · {p.legal_entity_name}</p>
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 text-xs">
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((x) => x - 1)} data-testid="plants-prev">‹</Button>
        <span data-testid="plants-page-info">{page} / {data.pages}</span>
        <Button size="sm" variant="outline" disabled={page >= data.pages} onClick={() => setPage((x) => x + 1)} data-testid="plants-next">›</Button>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="plant-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? editing.name : t("add")}</DialogTitle>
            <DialogDescription>{t("iam_plant_help")}</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">{t("iam_plant_code")} *</Label>
                <Input value={editing.code || ""} onChange={(e) => setEditing((p) => ({ ...p, code: e.target.value }))} data-testid="plant-code-input" /></div>
              <div className="space-y-1"><Label className="text-xs">{t("name")} *</Label>
                <Input value={editing.name || ""} onChange={(e) => setEditing((p) => ({ ...p, name: e.target.value }))} data-testid="plant-name-input" /></div>
              <div className="space-y-1"><Label className="text-xs">{t("iam_legal_entities")}</Label>
                <Select value={editing.legal_entity_id || "none"} onValueChange={(v) => setEditing((p) => ({ ...p, legal_entity_id: v === "none" ? "" : v }))}>
                  <SelectTrigger data-testid="plant-entity-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">–</SelectItem>
                    {entities.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1"><Label className="text-xs">{t("region")}</Label>
                <Select value={editing.region_id || "none"} onValueChange={(v) => setEditing((p) => ({ ...p, region_id: v === "none" ? "" : v }))}>
                  <SelectTrigger data-testid="plant-region-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">–</SelectItem>
                    {regions.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1"><Label className="text-xs">{t("iam_country")}</Label>
                <Input value={editing.country || ""} onChange={(e) => setEditing((p) => ({ ...p, country: e.target.value }))} data-testid="plant-country-input" /></div>
              <div className="space-y-1"><Label className="text-xs">{t("iam_city")}</Label>
                <Input value={editing.city || ""} onChange={(e) => setEditing((p) => ({ ...p, city: e.target.value }))} data-testid="plant-city-input" /></div>
              <div className="space-y-1"><Label className="text-xs">{t("iam_timezone")}</Label>
                <Select value={editing.timezone || "none"} onValueChange={(v) => setEditing((p) => ({ ...p, timezone: v === "none" ? "" : v }))}>
                  <SelectTrigger data-testid="plant-tz-input"><SelectValue placeholder="–" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">–</SelectItem>
                    {plOptions("timezones").map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1"><Label className="text-xs">{t("iam_plant_manager")}</Label>
                <Select value={editing.manager_id || "none"} onValueChange={(v) => setEditing((p) => ({ ...p, manager_id: v === "none" ? "" : v }))}>
                  <SelectTrigger data-testid="plant-manager-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">–</SelectItem>
                    {users.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1"><Label className="text-xs">{t("status")}</Label>
                <Select value={editing.status || "active"} onValueChange={(v) => setEditing((p) => ({ ...p, status: v }))}>
                  <SelectTrigger data-testid="plant-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="active">{t("iam_status_active")}</SelectItem>
                    <SelectItem value="inactive">{t("iam_status_inactive")}</SelectItem></SelectContent>
                </Select></div>
              <div className="space-y-1 sm:col-span-2"><Label className="text-xs">{t("address")}</Label>
                <Input value={editing.address || ""} onChange={(e) => setEditing((p) => ({ ...p, address: e.target.value }))} data-testid="plant-address-input" /></div>
              <div className="sm:col-span-2 flex flex-wrap gap-1.5">
                {(picklists?.segments || []).map((s) => (
                  <button key={s} type="button" data-testid={`plant-segment-${s}`}
                    onClick={() => setEditing((p) => ({ ...p, segments: (p.segments || []).includes(s)
                      ? p.segments.filter((x) => x !== s) : [...(p.segments || []), s] }))}
                    className={`px-2.5 h-7 rounded-full text-[11px] border ${(editing.segments || []).includes(s)
                      ? "bg-[#E8871E] text-white border-[#E8871E]" : "bg-white text-gray-600 border-gray-200"}`}>{s}</button>
                ))}
              </div>
              <Button className="sm:col-span-2 bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" onClick={save} data-testid="plant-save-btn">{t("save")}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OrgTab() {
  const { t } = useApp();
  const [org, setOrg] = useState({});
  useEffect(() => { api.get("/iam/organization").then((r) => setOrg(r.data || {})).catch(() => {}); }, []);
  const save = async () => {
    try { await api.put("/iam/organization", org); toast.success("✓"); }
    catch (e) { toast.error(errDetail(e)); }
  };
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl" data-testid="org-tab">
      <div className="space-y-1 sm:col-span-2"><Label className="text-xs">{t("name")}</Label>
        <Input value={org.name || ""} onChange={(e) => setOrg((o) => ({ ...o, name: e.target.value }))} data-testid="org-name-input" /></div>
      <div className="space-y-1"><Label className="text-xs">{t("iam_default_language")}</Label>
        <Input value={org.default_language || ""} onChange={(e) => setOrg((o) => ({ ...o, default_language: e.target.value }))} data-testid="org-lang-input" /></div>
      <div className="space-y-1"><Label className="text-xs">{t("iam_default_currency")}</Label>
        <Input value={org.default_currency || ""} onChange={(e) => setOrg((o) => ({ ...o, default_currency: e.target.value }))} data-testid="org-currency-input" /></div>
      <div className="space-y-1 sm:col-span-2"><Label className="text-xs">Logo URL</Label>
        <Input value={org.logo_url || ""} onChange={(e) => setOrg((o) => ({ ...o, logo_url: e.target.value }))} data-testid="org-logo-input" /></div>
      <Button className="sm:col-span-2 bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" onClick={save} data-testid="org-save-btn">{t("save")}</Button>
    </div>
  );
}

export default function OrgStructure() {
  const { t } = useApp();
  return (
    <div className="space-y-4" data-testid="iam-org-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#1B2A4A]" style={{ fontFamily: "Work Sans" }}>{t("iam_org")}</h1>
        <p className="text-xs text-gray-500">{t("iam_org_sub")}</p>
      </div>
      <Tabs defaultValue="plants">
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="plants" data-testid="org-tab-plants"><Factory className="h-3.5 w-3.5 mr-1" />{t("iam_plants")}</TabsTrigger>
          <TabsTrigger value="entities" data-testid="org-tab-entities"><Building2 className="h-3.5 w-3.5 mr-1" />{t("iam_legal_entities")}</TabsTrigger>
          <TabsTrigger value="regions" data-testid="org-tab-regions"><Globe2 className="h-3.5 w-3.5 mr-1" />{t("iam_regions")}</TabsTrigger>
          <TabsTrigger value="org" data-testid="org-tab-organization">{t("iam_organization")}</TabsTrigger>
        </TabsList>
        <TabsContent value="plants" className="pt-4"><PlantsTab /></TabsContent>
        <TabsContent value="entities" className="pt-4">
          <SimpleList path="legal-entities" fields={["name", "country", "vat_id"]}
            labelKeys={["name", "iam_country", "iam_vat_id"]} testid="entities-list" />
        </TabsContent>
        <TabsContent value="regions" className="pt-4">
          <SimpleList path="regions" fields={["name", "description"]}
            labelKeys={["name", "iam_description"]} testid="regions-list" />
        </TabsContent>
        <TabsContent value="org" className="pt-4"><OrgTab /></TabsContent>
      </Tabs>
    </div>
  );
}
